import type {
  AboveHeaderBlock,
  AdminPanelResponse,
  ContactsBlock,
  DesktopMenuItem,
  FooterMenuItem,
  LoginSmsToggler,
  MobileMenuItem,
  RecommendationBlock,
  SettingsItem,
  SocialNetworksLinks,
  YandexPay,
} from '~/types/SiteSettings';
import type { Sitemap } from '~/types/Sitemap';

export const useSiteStore = createSiteStore();

export function createSiteStore() {
  return defineStore('site', {
    state: () => ({
      contacts: {
        phone: '' as string,
        tgLink: '' as string,
        copyrightText: '' as string,
      },
      socialNetworkLinks: [] as SocialNetworksLinks,
      aboveHeaderBlock: {
        text: '' as string,
        url: '' as string,
        isActive: false as boolean,
      },
      recommendationBlocks: [] as RecommendationBlock[],
      isShowAboveHeaderBlock: false,
      isShowHeader: true,
      isSmsLoginEnabled: true,
      currentPageBreadcrumb: '',
      isMobileMenuOpen: false,
      mobileMenuExpandedIds: [] as number[],
      isYandexPayReady: false,
      yandexSplitThreshold: null as number | null,
    }),

    actions: {
      async getDesktopMenu() {
        try {
          const { data } = await useCustomFetch<DesktopMenuItem[]>('/layout/desktop-menu', { serverCacheTtl: 60_000 });

          if (data.value) {
            return data.value;
          } else {
            return [];
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return [];
        }
      },

      async getMobileMenu() {
        try {
          const { data } = await useCustomFetch<MobileMenuItem[]>('/layout/mobile-menu', { serverCacheTtl: 60_000 });

          if (data.value) {
            return data.value;
          } else {
            return [];
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return [];
        }
      },

      async getFooterMenu() {
        try {
          const { data } = await useCustomFetch<FooterMenuItem[]>('/layout/footer-menu', { serverCacheTtl: 60_000 });

          if (data.value) {
            return data.value;
          }
          return [];
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return [];
        }
      },

      async getSiteSettings() {
        try {
          const { data } = await useCustomFetch<SettingsItem[]>('/layout/settings', { serverCacheTtl: 60_000 });

          if (data.value) {
            const siteSettings = data.value;
            const mappedSettings: { [key: string]: SettingsItem } = {};

            for (const i in siteSettings) {
              if (siteSettings[i]) mappedSettings[siteSettings[i].slug] = siteSettings[i];
            }

            if (mappedSettings.yandex_pay?.fields) {
              this.setYandexSplitThreshold(mappedSettings.yandex_pay.fields as YandexPay);
            }
            if (mappedSettings.footer_menu_information?.fields) {
              this.setContactsBlockData(mappedSettings.footer_menu_information.fields as ContactsBlock);
            }
            if (mappedSettings.footer_menu_social_networks?.fields) {
              this.setSocialNetworksLinks(mappedSettings.footer_menu_social_networks.fields as SocialNetworksLinks);
            }
            if (mappedSettings.header_old_site?.fields) {
              this.setAboveHeaderBlockData(mappedSettings.header_old_site.fields as AboveHeaderBlock);
            }
            if (mappedSettings.login_sms_toggler?.fields) {
              this.setLoginSmsToggler(mappedSettings.login_sms_toggler.fields as LoginSmsToggler);
            }
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async getAdminPanelData() {
        try {
          const { data } = await useCustomFetch<{ data: AdminPanelResponse }>('/adminbar');

          if (data.value) {
            return data.value.data;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getRecommendationBlocks() {
        try {
          const { data } = await useCustomFetch<RecommendationBlock[]>(
            '/recommendation-blocks',
            { serverCacheTtl: 60_000 },
            '2',
          );

          if (data.value) {
            this.recommendationBlocks = data.value;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async getSitemap() {
        try {
          const { data } = await useCustomFetch<Sitemap>('/sitemap');

          if (data.value) {
            return data.value;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      setYandexSplitThreshold(fields: YandexPay) {
        this.yandexSplitThreshold = fields.split_min_sum ?? null;
      },

      setContactsBlockData(fields: ContactsBlock) {
        this.contacts = {
          phone: fields.phone ?? '',
          tgLink: fields.question_url ?? '',
          copyrightText: fields.copyright_text ?? '',
        };
      },

      setSocialNetworksLinks(links: SocialNetworksLinks) {
        this.socialNetworkLinks = links;
      },

      setAboveHeaderBlockData(fields: AboveHeaderBlock) {
        this.aboveHeaderBlock = {
          text: fields.text ?? '',
          url: fields.url ?? '',
          isActive: fields.is_active ?? false,
        };
      },

      setLoginSmsToggler(fields: LoginSmsToggler) {
        this.isSmsLoginEnabled = fields.enabled ?? true;
      },

      hideAboveHeaderBlock() {
        this.isShowAboveHeaderBlock = false;
        sessionStorage.setItem('oldSiteLinkShow', 'false');
      },

      toggleMobileMenu() {
        this.isMobileMenuOpen = !this.isMobileMenuOpen;
        this.toggleBodyScrollLock(!!this.isMobileMenuOpen);
      },

      toggleBodyScrollLock(lockState = false) {
        const body = document.querySelector('body');

        if (lockState) {
          if (body) {
            body.style.overflow = 'hidden';
            body.style.paddingRight = 'var(--scrollbar-width)';
          }
        } else {
          body?.removeAttribute('style');
        }
      },
    },
  });
}
